import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const jwtSchema = z
  .string()
  .min(1)
  .regex(/^[\w-]+\.[\w-]+\.[\w-]+$/, "Invalid JWT format");

// ── Payload Types ─────────────────────────────────────────
export interface HealthMetricEntry {
  id: string;
  user_id: string;
  date: string;
  weight_kg: number | null;
  blood_pressure_systolic: number | null;
  blood_pressure_diastolic: number | null;
  sleep_hours: number | null;
  mood: string | null;
  energy_level: number | null;
  stress_level: number | null;
  steps: number | null;
}

export interface HealthOverviewPayload {
  totalUsers: number;
  recentMetrics: HealthMetricEntry[];
  avgMood: number;
  avgEnergy: number;
  avgSleep: number;
}

// ── Server Functions ──────────────────────────────────────
export const getHealthOverview = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      accessToken: jwtSchema,
    })
  )
  .handler(async ({ data }) => {
    const { createAdminClient } = await import("./_admin-utils.server");
    const { client, error: initError } = await createAdminClient();
    if (!client) return { data: null, error: initError ?? "Missing server env vars" };

    const { data: { user }, error: authError } = await client.auth.getUser(data.accessToken);
    if (authError || !user) return { data: null, error: "Unauthorized" };

    const { data: metrics, error } = await client
      .from("health_metric_entries")
      .select("id, user_id, date, weight_kg, blood_pressure_systolic, blood_pressure_diastolic, sleep_hours, mood, energy_level, stress_level, steps")
      .order("date", { ascending: false })
      .limit(50);

    if (error) return { data: null, error: error.message };

    const list = (metrics ?? []) as Array<Record<string, unknown>>;
    const uniqueUsers = new Set(list.map((m) => String(m.user_id)));

    const energyValues = list.map((m) => Number(m.energy_level)).filter((v) => !isNaN(v) && v > 0);
    const sleepValues = list.map((m) => Number(m.sleep_hours)).filter((v) => !isNaN(v) && v > 0);

    const avgEnergy = energyValues.length > 0
      ? Math.round(energyValues.reduce((a, b) => a + b, 0) / energyValues.length * 10) / 10
      : 0;
    const avgSleep = sleepValues.length > 0
      ? Math.round(sleepValues.reduce((a, b) => a + b, 0) / sleepValues.length * 10) / 10
      : 0;

    const recentMetrics: HealthMetricEntry[] = list.slice(0, 20).map((m) => ({
      id: String(m.id),
      user_id: String(m.user_id),
      date: String(m.date),
      weight_kg: m.weight_kg != null ? Number(m.weight_kg) : null,
      blood_pressure_systolic: m.blood_pressure_systolic != null ? Number(m.blood_pressure_systolic) : null,
      blood_pressure_diastolic: m.blood_pressure_diastolic != null ? Number(m.blood_pressure_diastolic) : null,
      sleep_hours: m.sleep_hours != null ? Number(m.sleep_hours) : null,
      mood: m.mood ? String(m.mood) : null,
      energy_level: m.energy_level != null ? Number(m.energy_level) : null,
      stress_level: m.stress_level != null ? Number(m.stress_level) : null,
      steps: m.steps != null ? Number(m.steps) : null,
    }));

    return {
      data: {
        totalUsers: uniqueUsers.size,
        recentMetrics,
        avgMood: 0,
        avgEnergy,
        avgSleep,
      },
      error: null,
    };
  });
